import { styled } from "goober";
import { useState } from "react";
import type * as React from "react";
import { useRecordListControlChange } from "../hooks/useRecordListControlChange";
import { useScrollLock } from "../hooks/useScrollLock";
import { Button } from "./Button";
import { MIN_OPTIONS_FOR_SEARCH } from "./SearchableDropdown";
import { Dropdown } from "./Dropdown";
import { Icon } from "./Icon";
import { PressButton } from "./PressButton";
import { Text } from "./Text";
import { DefaultIcon, HoverIcon, ToggleIconContainer } from "./ToggleIcon";

type FilterOption = {
  id: string;
  label: string;
  menuLeft?: React.ReactNode;
  pillLeft?: React.ReactNode;
  subtitle?: React.ReactNode;
  searchText?: string;
};

export type FilterDef =
  | {
      kind: "single";
      key: string;
      label: string;
      icon: React.ReactNode;
      options: FilterOption[];
      value: string | null;
      defaultValue?: string;
      onSelect: (id: string) => void;
      onClear: () => void;
    }
  | {
      kind: "multi";
      key: string;
      label: string;
      icon: React.ReactNode;
      options: FilterOption[];
      values: string[];
      onToggle: (id: string) => void;
    };

const FilterButtonWrapper = styled("div")`
  position: relative;
  display: inline-flex;
`;

const FilterActiveDot = styled("div")`
  position: absolute;
  top: -2px;
  right: -2px;
  padding: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--surface-primary);

  &::after {
    content: "";
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--red-600);
  }
`;

const FilterBadge = styled(PressButton)`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 20px;
  padding: 0 8px 0 6px;
  background: var(--alpha4);
  border: none;
  border-radius: 14px;
  color: var(--text-secondary);
  cursor: pointer;
  &:hover {
    background: var(--alpha8);
    ${DefaultIcon} {
      opacity: 0;
      transform: rotate(90deg) scale(0.5);
    }
    ${HoverIcon} {
      opacity: 1;
      transform: rotate(0deg) scale(1);
    }
  }
`;

export function hasActiveFilters(filters: FilterDef[]) {
  return filters.some((f) =>
    f.kind === "single" ? f.value != null : f.values.length > 0
  );
}

export function ListFilter({ filters }: { filters: FilterDef[] }) {
  const recordControl = useRecordListControlChange();
  const active = hasActiveFilters(filters);
  const [open, setOpen] = useState(false);
  useScrollLock(open);
  return (
    <Dropdown
      onOpenChange={setOpen}
      opener={
        <FilterButtonWrapper>
          <Button variant="secondary" icon="list-filter" />
          {active ? <FilterActiveDot /> : null}
        </FilterButtonWrapper>
      }
      align="bottom"
      minWidth={180}
      sections={[
        {
          items: filters.map((filter) => ({
            type: "submenu" as const,
            left: filter.icon,
            label: filter.label,
            disabled: filter.options.length === 0,
            searchable: filter.options.length >= MIN_OPTIONS_FOR_SEARCH,
            submenu: [
              {
                items: filter.options.map((option) => ({
                  type: "button" as const,
                  key: option.id,
                  left: option.menuLeft ?? null,
                  label: option.label,
                  subtitle: option.subtitle,
                  searchText: option.searchText,
                  right: (
                    <Icon
                      name="check"
                      size={12}
                      color={
                        (
                          filter.kind === "single"
                            ? (filter.value ?? filter.defaultValue ?? null) ===
                              option.id
                            : filter.values.includes(option.id)
                        )
                          ? "var(--text-secondary)"
                          : "transparent"
                      }
                    />
                  ),
                  onClick: () => {
                    const control = `filter_${filter.key}`;
                    if (filter.kind === "single") {
                      const selected =
                        (filter.value ?? filter.defaultValue ?? null) ===
                        option.id;
                      recordControl(
                        control,
                        selected ? `remove:${option.id}` : option.id
                      );
                      if (selected) filter.onClear();
                      else filter.onSelect(option.id);
                    } else {
                      recordControl(
                        control,
                        filter.values.includes(option.id)
                          ? `remove:${option.id}`
                          : option.id
                      );
                      filter.onToggle(option.id);
                    }
                  },
                })),
              },
            ],
          })),
        },
      ]}
    />
  );
}

const centeredDefaultIconStyle: React.CSSProperties = {
  inset: 0,
  alignItems: "center",
  justifyContent: "center",
};

export function ListFilterPills({ filters }: { filters: FilterDef[] }) {
  const recordControl = useRecordListControlChange();
  if (!hasActiveFilters(filters)) return null;
  return (
    <>
      <Text variant="text-small" color="var(--text-secondary)">
        Filtered by:
      </Text>
      {filters.flatMap((filter) => {
        if (filter.kind === "single") {
          if (filter.value == null) return [];
          const option = filter.options.find((o) => o.id === filter.value);
          if (!option) return [];
          return [
            <FilterBadge
              key={`${filter.key}:${option.id}`}
              onPress={() => {
                recordControl(`filter_${filter.key}`, `remove:${option.id}`);
                filter.onClear();
              }}
              title="Clear filter"
            >
              <ToggleIconContainer>
                <DefaultIcon style={centeredDefaultIconStyle}>
                  {option.pillLeft ?? option.menuLeft}
                </DefaultIcon>
                <HoverIcon>
                  <Icon name="x" size={12} color="var(--text-secondary)" />
                </HoverIcon>
              </ToggleIconContainer>
              <Text variant="text-smaller-md" color="var(--text-secondary)">
                {option.label}
              </Text>
            </FilterBadge>,
          ];
        }
        return filter.values
          .map((id) => {
            const option = filter.options.find((o) => o.id === id);
            if (!option) return null;
            return (
              <FilterBadge
                key={`${filter.key}:${id}`}
                onPress={() => {
                  recordControl(`filter_${filter.key}`, `remove:${id}`);
                  filter.onToggle(id);
                }}
                title="Remove filter"
              >
                <ToggleIconContainer>
                  <DefaultIcon style={centeredDefaultIconStyle}>
                    {option.pillLeft ?? option.menuLeft}
                  </DefaultIcon>
                  <HoverIcon>
                    <Icon name="x" size={12} color="var(--text-secondary)" />
                  </HoverIcon>
                </ToggleIconContainer>
                <Text variant="text-smaller-md" color="var(--text-secondary)">
                  {option.label}
                </Text>
              </FilterBadge>
            );
          })
          .filter((v): v is NonNullable<typeof v> => v != null);
      })}
    </>
  );
}

export function parseMultiFilter(param: string | undefined): string[] {
  return param && param !== "all" ? param.split(",").filter(Boolean) : [];
}

export function toggleMultiFilter(values: string[], id: string): string {
  const next = values.includes(id)
    ? values.filter((v) => v !== id)
    : [...values, id];
  return next.length === 0 ? "all" : next.join(",");
}
